import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import {
  Program,
  ServicePlan,
  ServicePlanServiceArea,
} from '../../../data/models/client-program.model';
import { DateTime } from 'luxon';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'ServicePlanComponent';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  servicePlan: `${PREFIX}-servicePlan`,
  purposeCodes: `${PREFIX}-purposeCodes`,
  chip: `${PREFIX}-chip`,
  pcchip: `${PREFIX}-pcchip`,
  sachip: `${PREFIX}-sachip`,
  pmchip: `${PREFIX}-pmchip`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    padding: theme.spacing(1),
    pointerEvents: 'none',
  },

  [`& .${classes.title}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& p': {
      fontWeight: 700,
    },
    '& span': {
      pointerEvents: 'all',
    },
  },

  [`& .${classes.servicePlan}`]: {
    padding: theme.spacing(1),
  },

  [`& .${classes.purposeCodes}`]: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    pointerEvents: 'none',
  },

  [`& .${classes.chip}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.palette.primary.dark,
    '& .MuiChip-deleteIcon': {
      color: theme.palette.primary.dark,
      pointerEvents: 'all',
    },
  },

  [`& .${classes.pcchip}`]: {
    backgroundColor: '#bce1f5',
    'span.MuiChip-label': {
      color: `${theme.palette.primary.dark}`,
    },
  },

  [`& .${classes.sachip}`]: {
    backgroundColor: '#bcf5e1',
    'span.MuiChip-label': {
      color: `${theme.palette.primary.dark}`,
    },
  },

  [`& .${classes.pmchip}`]: {
    backgroundColor: '#d9ffcc',
    'span.MuiChip-label': {
      color: `${theme.palette.primary.dark}`,
    },
  },
}));

interface ServicePlanComponentProps {
  program: Program;
  servicePlan: ServicePlan;
  purposeCodes: ClientPurposeCode[];
  serviceAreas: ServicePlanServiceArea[];
  transportationBenefits: TransportationBenefit[];
}

export default function ServicePlanComponent({
  program,
  servicePlan,
  purposeCodes,
  serviceAreas,
  transportationBenefits,
}: ServicePlanComponentProps) {
  const t = useCommonTranslation();

  const renderDate = (date?: string): React.ReactElement => {
    let dt = '';
    if (date) {
      dt = DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT);
    }
    return <>{dt}</>;
  };

  return (
    <StyledPaper
      elevation={1}
      key={servicePlan.servicePlanUUID}
      className={classes.container}>
      <Box className={classes.title}>
        <Typography>
          {servicePlan.name} {renderDate(servicePlan.effectiveStartDate)}
        </Typography>
      </Box>
      <Box className={classes.purposeCodes}>
        {servicePlan.purposeCodeUUIDs?.map((purposeCodeUUID) => {
          const purposeCode = purposeCodes.find(
            (pc) => pc.purposeCodeUUID === purposeCodeUUID,
          );
          return purposeCode ? (
            <Chip
              color="primary"
              className={`${classes.chip} ${classes.pcchip}`}
              label={purposeCode.name}
              key={purposeCode.purposeCodeUUID}
            />
          ) : null;
        })}
        {servicePlan.serviceAreaUUIDs?.map((serviceAreaUUID) => {
          const serviceArea = serviceAreas.find(
            (sa) => sa.serviceAreaUUID === serviceAreaUUID,
          );
          return serviceArea ? (
            <Chip
              color="primary"
              className={`${classes.chip} ${classes.sachip}`}
              label={serviceArea.name}
              key={serviceArea.serviceAreaUUID}
            />
          ) : null;
        })}
        {servicePlan.transportationBenefitUUIDs?.map(
          (transportationBenefitUUID) => {
            const transportationBenefit = transportationBenefits.find(
              (pm) =>
                pm.transportationBenefitUUID === transportationBenefitUUID,
            );
            return transportationBenefit ? (
              <Chip
                color="primary"
                className={`${classes.chip} ${classes.pmchip}`}
                label={transportationBenefit.name}
                key={transportationBenefit.transportationBenefitUUID}
              />
            ) : null;
          },
        )}
      </Box>
    </StyledPaper>
  );
}
